import React, { forwardRef, useImperativeHandle, useState } from "react";

import { AttributeRef } from "./AddAttribute";
import { AddAttributeProps } from "../../utils/Interfaces";
import { ToggleSwitch } from "../../components/utils/ToggleSwitch";
import { FloatingInput } from "../../components/utils/FloatingInput";
import { FloatingTextArea } from "../../components/utils/FloatingTextArea";
import { G_onInputChangeHandler, G_onInputBlurHandler } from "../../components/utils/InputHandlers";

export const DateAttribute = forwardRef<AttributeRef, AddAttributeProps>(({ whatToAdd, posting }, ref) => {
    const [state, setstate] = useState({
        httpStatus: 200,
        input: {
            name: '',
            subtitle: '',
            mandatory: false,
        },
        errors: {
            name: '',
            subtitle: '',
            restrictions: '',
        }
    })

    const [subtitleVisibility, setSubtitleVisibility] = useState(false)

    const onChangeHandler = (e: any) => {
        if (!posting) {
            let { input } = state
            let { errors } = state
            let output: any = G_onInputChangeHandler(e)

            const updatedInput = {
                ...input,
                [e.target.name]: output.value
            };

            const updatedErrors = {
                ...errors,
                [e.target.name]: output.error
            };

            setstate((prev) => ({
                ...prev,
                input: updatedInput,
                errors: updatedErrors,
            }));
        }
    }

    const targetLength = (name: string) => {
        switch (name) {
            case 'subtitle':
                return {
                    min: 5,
                    max: 100
                };

            default:
                return {
                    min: 3,
                    max: 30
                };
        }
    }

    const onInputBlur = (e: any) => {
        if (!posting) {
            let { input } = state
            let { errors } = state

            const target0 = targetLength(e.target.name)
            let output: any = G_onInputBlurHandler(e, '', target0.min, target0.max)

            const updatedInput = {
                ...input,
                [e.target.name]: output.value
            };

            const updatedErrors = {
                ...errors,
                [e.target.name]: output.error
            };

            setstate((prev) => ({
                ...prev,
                input: updatedInput,
                errors: updatedErrors,
            }));
        }
    }

    const removeSubtitle = () => {
        setSubtitleVisibility(false)

        setstate((prev) => ({
            ...prev,
            input: {
                ...prev.input,
                subtitle: ''
            }, errors: {
                ...prev.errors,
                subtitle: ''
            }
        }))
    }

    const handleCheckboxChange = (e: any) => {
        setstate((prev) => ({
            ...prev,
            input: {
                ...prev.input,
                [e.target.name]: e.target.checked
            }
        }))
    }

    useImperativeHandle(ref, () => ({
        integrityCheck: (e: any) => {
            let { input } = state
            const inputArray = Object.keys(input)

            let passedValidation = true
            let attributes = input

            inputArray.forEach((inputObject: any) => {
                let oTarget = e.target[inputObject]
                let event: any = {
                    target: {
                        name: inputObject,
                    }
                }

                switch (inputObject) {
                    case 'subtitle':
                        if (subtitleVisibility) {
                            event.target.value = oTarget.value.trim()
                            event.target.required = oTarget.required

                            onInputBlur(event)
                        }
                        break

                    case 'mandatory':
                    case 'multiline':
                    case 'restrictions':
                        event.target.value = input[inputObject]
                        event.target.required = false
                        break

                    default:
                        event.target.value = oTarget.value.trim()
                        event.target.required = oTarget.required

                        onInputBlur(event)
                        break;
                }
            })

            let { errors } = state
            const errorArray = Object.keys(errors)

            errorArray.forEach((errorObject) => {
                if (errors[errorObject].length > 0) {
                    passedValidation = false
                }
            })

            return {
                passedValidation,
                attributes
            }
        },
    }));

    return (
        <React.Fragment>
            <div className="w-full mb-3">
                <p className="text-lg text-slate-900 dark:text-white flex items-center font-medium gap-x-3 py-2">
                    <span className={`fa-light ${whatToAdd.icon} fa-lg`}></span>
                    {whatToAdd.name}
                </p>

                <p className="text-sm pb-1 pt-1">
                    Capture date specific inputs
                </p>
            </div>

            <div className="w-full flex md:flex-row flex-col items-baseline md:gap-x-2">
                <div className="md:w-3/4 w-full">
                    <FloatingInput
                        name={"name"}
                        label={"Date to be captured"}
                        required={true}
                        posting={posting}
                        value={state.input.name}
                        error={state.errors.name}
                        blurHandler={onInputBlur}
                        changeHandler={onChangeHandler}
                    />
                </div>

                <div className="flex-grow md:flex justify-center w-full md:w-auto">
                    <button type="button" onClick={() => setSubtitleVisibility(true)} className={`text-theme pt-3 md:py-0 hover:text-orange-700 ${subtitleVisibility ? 'hidden' : 'block md:mb-0 mb-3'}`}>
                        <span className="text-sm">Add Subtitle</span>
                    </button>
                </div>
            </div>

            {
                subtitleVisibility ? (
                    <>
                        <div className="mt-3 md:py-0 md:pt-2 py-3 w-full bg-sky-00">
                            <FloatingTextArea
                                name={"subtitle"}
                                label={"Why is this data needed?"}
                                required={false}
                                posting={posting}
                                value={state.input.subtitle}
                                error={state.errors.subtitle}
                                blurHandler={onInputBlur}
                                changeHandler={onChangeHandler}
                            />

                            <div className="w-full md:w-auto flex flex-row-reverse">
                                <button type="button" onClick={() => removeSubtitle()} className={`text-theme md:py-0 hover:text-orange-700`}>
                                    <span className="text-sm">Remove Subtitle</span>
                                </button>
                            </div>
                        </div>
                    </>
                ) : null
            }

            <div className="w-full py-4">
                <ToggleSwitch
                    label={'Mandatory'}
                    name={'mandatory'}
                    disabled={false}
                    changeHandler={handleCheckboxChange}
                    checked={state.input.mandatory}
                    description={"Ensures users provide a value before submitting."}
                />
            </div>
        </React.Fragment>
    )
})